import React, {useMemo, useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {Link} from 'react-router-dom'
import {BsCalendar2Event, BsChevronLeft, BsChevronRight, BsDoorOpen, BsPersonFill} from 'react-icons/bs'
import {IoCalendarOutline, IoChevronDown, IoListOutline, IoTodayOutline} from 'react-icons/io5'
import useTranslate from 'modules/local/useTranslate'
import useAsync from 'modules/asyncCache/useAsync'
import {getResponseItems} from 'views/Shared'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {timetable_getsByClassesOwner_Api} from 'apis'
import {buildCourseRelLink} from '../functions/courseWorkspaceLinks'
import MyClassSessionsPanel from '../../../AppLocals/Course/CourseClass/tabs/MyClassSessionsPanel'

// "Thống Kê Lịch Học & Phòng Học" - khớp mockup bạn gửi, dữ liệu THẬT từ timetable_getsByClassesOwner_Api
// (CÙNG API UpcomingSchedule/CourseDashboard.js đang dùng - không tạo endpoint riêng). Mỗi Timetable
// trả về mang theo timetable_details[] (từng buổi cụ thể đã sinh từ lịch định kỳ) - gộp phẳng lại
// đúng cách UpcomingSchedule đang làm, KHÔNG lọc is_holiday khỏi field hiển thị mà đánh dấu riêng.
//
// "X/Y Có mặt" (sĩ số điểm danh) trong mockup CHƯA đưa vào - cần thêm 1 lượt gọi
// attendance-detail/gets RIÊNG cho mỗi buổi (Attendance record khác record với timetable_detail,
// phải tra qua course_class_timetable_detail_id trước), việc riêng cần bàn thêm - card hiện tại chỉ
// còn giờ học/phòng/giảng viên (đều là field thật).
//
// Khi nhúng cho ĐÚNG 1 lớp cụ thể (course_class_id - chỉ CourseClassTimetableTab.js truyền, các nơi
// khác dùng course_id/org-wide không có), bộ nút đổi hẳn sang CLASS_VIEW_MODES với pane "Sắp tới"
// (key riêng 'upcoming', KHÔNG tái dùng 'today') hiện THẲNG MyClassSessionsPanel - theo yêu cầu gộp
// nội dung "Lịch chi tiết" (trạng thái điểm danh + tài liệu + ghi chú từng buổi) vào đây thay vì để
// riêng 1 tab trùng lặp. KHÔNG đổi TodayView (today thật, lọc is_holiday + đúng ngày) cho các nơi
// org-wide/course_id khác vẫn dùng component này (CourseDashboard.js, CourseClassOperations.js,
// Workspace.js...) - tránh đổi hành vi nơi không được yêu cầu. MyClassSessionsPanel tự fetch data
// riêng qua CourseClassContext (không đọc `sessions` của panel này) - hợp lệ vì course_class_id chỉ
// xuất hiện khi cha đã đứng trong CourseClassProvider (CourseClassPage.js).

const ORANGE = '#c2410c'
const NAVY_900 = '#1c4a75'

const VIEW_MODES = [
    {key: 'today', label: 'today schedule', icon: IoTodayOutline},
    {key: 'list', label: 'list view', icon: IoListOutline},
    {key: 'calendar', label: 'calendar view', icon: IoCalendarOutline},
]

// Bộ nút RIÊNG cho ngữ cảnh 1 lớp cụ thể (course_class_id - chỉ CourseClassTimetableTab.js truyền)
// - key 'upcoming' KHÁC HẲN 'today' (không tái dùng chung key rồi rẽ nhánh if/else theo isSingleClass
// nữa) để phần render bên dưới khỏi phải case-when lồng nhau, tách bạch rõ 2 bộ nút độc lập.
const CLASS_VIEW_MODES = [
    {key: 'upcoming', label: 'upcoming schedule tab', icon: IoTodayOutline},
    {key: 'list', label: 'list view', icon: IoListOutline},
    {key: 'calendar', label: 'calendar view', icon: IoCalendarOutline},
]

const flattenSessions = (timetables) => timetables.flatMap((tt) => (
    (tt.timetable_details ?? []).map((detail) => ({
        ...detail,
        course_class: tt.course_class,
        teacher: tt.teacher,
        location_address: detail.location_address || tt.location_address,
        is_holiday: !!tt.is_holiday,
    }))
))

const formatDayLabel = (t, date) => `${t(`day_short_${date.day()}`)}, ${date.format('DD/MM')}`

// ─── "Hôm nay" ──────────────────────────────────────────────────────────────

// hideActionLinks: bỏ link "Vào lớp"/"Xem chi tiết" khi nhúng ở ngữ cảnh KHÔNG có khái niệm
// ?rel=/access-portal (vd trang /classroom/:id công khai, xem CourseClassTimetableTab.js) -
// buildCourseRelLink giữ nguyên pathname hiện tại chỉ đổi query ?rel=, hợp lệ trong Workspace/
// access-portal nhưng VÔ NGHĨA ở trang classroom (route theo path, không đọc ?rel=), lại đang đứng
// SẴN trên đúng trang của lớp đó rồi (không cần điều hướng đi đâu nữa).
// showDate - "Chỉ hiển thị 1 buổi sắp tới gần nhất" (compactUpcomingOnly bên dưới) không chắc buổi
// đó rơi vào HÔM NAY (khác TodayView/ListView vốn tự có ngữ cảnh ngày riêng) - cần in kèm ngày, nếu
// không admin dễ hiểu lầm "hôm nay có lớp" trong khi thật ra là buổi của vài ngày tới.
const TodaySessionCard = ({session, t, hideActionLinks, showDate}) => {
    const isPast = dayjs(session.end_time).isBefore(dayjs())
    return (
        <div className="p-3 flex flex-col gap-2 background border border-color-50 rounded-2xl shadow-sm">
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-slate-700">
                    {showDate && `${formatDayLabel(t, dayjs(session.start_time))} · `}
                    {dayjs(session.start_time).format('HH:mm')} - {dayjs(session.end_time).format('HH:mm')}
                </span>
                {!!session.location_address && (
                    <span className="px-2 py-1 rounded-lg text-2xs font-bold bg-slate-100 text-slate-500 whitespace-nowrap">
                        {session.location_address}
                    </span>
                )}
            </div>
            <span className="font-bold text-sm text-slate-800 leading-snug max-lines-2">
                {session.course_class?.title || session.title}
            </span>
            {!!session.teacher?.name && (
                <span className="flex items-center gap-1.5 text-xs text-slate-400">
                    <BsPersonFill size={11}/>
                    {t('teacher')}: {session.teacher.name}
                </span>
            )}
            {!hideActionLinks && (
                <div className="mt-auto pt-1 flex items-center justify-end">
                    <Link
                        to={buildCourseRelLink({rel: 'course-class', 'class-id': session.course_class?.id, 'class-tab': 'schedule'})}
                        style={{color: isPast ? '#94a3b8' : ORANGE}}
                        className="font-semibold text-xs no-underline hover:opacity-75">
                        {isPast ? t('session ended') : t('enter class')} →
                    </Link>
                </div>
            )}
        </div>
    )
}

const TodayView = ({sessions, t, hideActionLinks}) => {
    const today = dayjs()
    const todaySessions = _.sortBy(
        sessions.filter((s) => !s.is_holiday && dayjs(s.start_time).isSame(today, 'day')),
        (s) => dayjs(s.start_time).valueOf()
    )

    return (
        <div className="flex flex-col gap-3">
            <div className="px-3 py-2.5 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-amber-50 border border-amber-100">
                <span className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                    <BsCalendar2Event size={14}/>
                    {t('teaching schedule today')}: {formatDayLabel(t, today)}
                </span>
                {todaySessions.length > 0 && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 whitespace-nowrap">
                        {todaySessions.length} {t('classes ongoing or upcoming')}
                    </span>
                )}
            </div>
            {todaySessions.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-300 italic">{t('no classes scheduled today')}</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {todaySessions.map((s) => <TodaySessionCard key={s.id} session={s} t={t} hideActionLinks={hideActionLinks}/>)}
                </div>
            )}
        </div>
    )
}

// ─── "Dạng List" ────────────────────────────────────────────────────────────

const ListView = ({sessions, t, hideActionLinks}) => {
    const upcoming = useMemo(() => (
        _.sortBy(
            sessions.filter((s) => !s.is_holiday && dayjs(s.end_time).isAfter(dayjs())),
            (s) => dayjs(s.start_time).valueOf()
        ).slice(0, 10)
    ), [sessions])

    if (!upcoming.length) {
        return <div className="py-8 text-center text-sm text-slate-300 italic">{t('no upcoming schedule')}</div>
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{borderCollapse: 'collapse'}}>
                <thead>
                    <tr className="border-b border-color-100">
                        {(hideActionLinks
                            ? ['day / date', 'study time', 'class / subject', 'room', 'teacher']
                            : ['day / date', 'study time', 'class / subject', 'room', 'teacher', 'action']
                        ).map((h) => (
                            <th key={h} className="py-2 px-2 text-left text-2xs font-bold text-slate-400 uppercase tracking-wide whitespace-nowrap">
                                {t(h)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {upcoming.map((s) => (
                        <tr key={s.id} className="border-b border-slate-50 last:border-b-0">
                            <td className="py-2.5 px-2 font-semibold whitespace-nowrap" style={{color: NAVY_900}}>
                                {formatDayLabel(t, dayjs(s.start_time))}
                            </td>
                            <td className="py-2.5 px-2 text-slate-600 whitespace-nowrap">
                                {dayjs(s.start_time).format('HH:mm')} - {dayjs(s.end_time).format('HH:mm')}
                            </td>
                            <td className="py-2.5 px-2 text-slate-700 font-medium max-w-xs truncate">
                                {s.course_class?.title || s.title}
                            </td>
                            <td className="py-2.5 px-2">
                                {!!s.location_address && (
                                    <span className="px-2 py-1 rounded-lg text-2xs font-bold bg-slate-100 text-slate-500 whitespace-nowrap">
                                        {s.location_address}
                                    </span>
                                )}
                            </td>
                            <td className="py-2.5 px-2 text-slate-600 whitespace-nowrap">{s.teacher?.name || '—'}</td>
                            {!hideActionLinks && (
                                <td className="py-2.5 px-2 whitespace-nowrap">
                                    <Link
                                        to={buildCourseRelLink({rel: 'course-class', 'class-id': s.course_class?.id, 'class-tab': 'schedule'})}
                                        style={{color: ORANGE}}
                                        className="font-semibold no-underline hover:opacity-75">
                                        {t('view detail')}
                                    </Link>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

// ─── "Dạng Calendar" ────────────────────────────────────────────────────────

const CALENDAR_TAG_COLORS = ['#c2410c', '#4338ca', '#0f766e', '#be185d', '#0369a1']
const colorForClass = (classId) => {
    if (!classId) return CALENDAR_TAG_COLORS[0]
    const hash = Array.from(String(classId)).reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
    return CALENDAR_TAG_COLORS[hash % CALENDAR_TAG_COLORS.length]
}

const CalendarView = ({sessions, t}) => {
    const [month, setMonth] = useState(() => dayjs().startOf('month'))

    const byDay = useMemo(() => {
        const map = {}
        sessions.filter((s) => !s.is_holiday && dayjs(s.start_time).isSame(month, 'month')).forEach((s) => {
            const key = dayjs(s.start_time).format('YYYY-MM-DD')
            map[key] = map[key] || []
            map[key].push(s)
        })
        return map
    }, [sessions, month])

    // Lưới bắt đầu Thứ 2 (T2) - khớp mockup, dayjs().day() 0=CN nên quy đổi (day + 6) % 7.
    const startOfGrid = month.startOf('month').subtract((month.startOf('month').day() + 6) % 7, 'day')
    const cells = Array.from({length: 42}, (_, i) => startOfGrid.add(i, 'day'))

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-sm font-bold text-slate-700">
                    <IoCalendarOutline size={15}/>
                    {t('month')} {month.format('MM, YYYY')}
                </span>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => setMonth((m) => m.subtract(1, 'month'))}
                        className="w-8 h-8 flex flex-center rounded-lg background-100 hover:background-200 border-0 cursor-pointer">
                        <BsChevronLeft size={13}/>
                    </button>
                    <button
                        type="button"
                        onClick={() => setMonth((m) => m.add(1, 'month'))}
                        className="w-8 h-8 flex flex-center rounded-lg background-100 hover:background-200 border-0 cursor-pointer">
                        <BsChevronRight size={13}/>
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
                {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                    <div key={d} className="text-center text-2xs font-bold text-slate-400 uppercase tracking-wide py-1">
                        {t(`day_short_${d}`)}
                    </div>
                ))}
                {cells.map((day) => {
                    const key = day.format('YYYY-MM-DD')
                    const daySessions = byDay[key] || []
                    const inMonth = day.isSame(month, 'month')
                    const isToday = day.isSame(dayjs(), 'day')
                    return (
                        <div
                            key={key}
                            style={isToday ? {borderColor: ORANGE, background: '#fff7ed'} : undefined}
                            className={classNames(
                                'p-1.5 min-h-16 flex flex-col gap-1 rounded-xl border',
                                isToday ? '' : 'border-color-50',
                                !inMonth && 'opacity-30',
                            )}>
                            <span className="text-xs font-semibold text-slate-500">{day.format('D')}</span>
                            {daySessions.slice(0, 2).map((s) => (
                                <span
                                    key={s.id}
                                    title={s.course_class?.title}
                                    style={{background: colorForClass(s.course_class?.id), color: '#fff'}}
                                    className="px-1.5 py-0.5 rounded text-2xs font-semibold truncate">
                                    {s.course_class?.title || s.title}
                                </span>
                            ))}
                            {daySessions.length > 2 && (
                                <span className="text-2xs text-slate-400">+{daySessions.length - 2}</span>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ─── Root ───────────────────────────────────────────────────────────────────

// course_id/course_class_id/eyebrow/title: cho phép nhúng LẠI ĐÚNG layout này ở nhiều ngữ cảnh hẹp
// hơn "toàn cơ sở" - course_id cho 1 khóa (CourseClassOperations.js), course_class_id cho 1 LỚP cụ
// thể (trang /classroom/:id tab Lịch trình, thay TimetableCalendar cũ - bạn yêu cầu dùng lại layout
// này). timetable_getsByClassesOwner_Api đã hỗ trợ sẵn cả 2 param (BE), không cần lọc lại ở client.
// Không truyền gì (CourseDashboard.js) giữ nguyên hành vi org-wide cũ; eyebrow/title override để chữ
// tiêu đề khớp ngữ cảnh (khác "toàn cơ sở"); hideActionLinks - xem comment TodaySessionCard.
//
// collapsible/compactUpcomingOnly: 2 prop MỚI, chỉ CourseClassOperations.js truyền (bạn yêu cầu
// riêng cho khối "Lịch Trình Cả Khóa" ở đó - đã có sẵn khối navy/amber lớn phía trên chiếm nhiều chỗ,
// không cần lịch học mở sẵn/đủ 3 chế độ Hôm nay-List-Calendar mặc định nữa). Mặc định CẢ 2 = false,
// KHÔNG đổi hành vi mọi nơi khác đang nhúng component này (CourseDashboard.js, trang /classroom/:id).
//   - collapsible: bọc khung viền hổ phách (khớp tông khối InfoBlock amber ngay phía trên ở
//     CourseClassOperations.js), THU GỌN mặc định, bấm vào header để mở/đóng.
//   - compactUpcomingOnly: bỏ hẳn bộ toggle Hôm nay/List/Calendar, chỉ tính VÀ hiện ĐÚNG 1 buổi học
//     sắp diễn ra gần nhất (không phân biệt hôm nay hay ngày sau) - trả lời nhanh "buổi kế tiếp là
//     khi nào" thay vì cả bảng/lịch đầy đủ.
//
// standaloneCard: prop MỚI, chỉ CourseDashboard.js truyền. Nhánh mặc định (không collapsible) vốn
// tự bỏ padding/nền/viền ở mobile RỒI BỎ LẠI PADDING lần 2 từ lg (`lg:p-0`) - tuned riêng cho
// CourseClassTimetableTab.js (nhúng lồng trong CourseClassPage.js, đã tự có padding trang, tránh
// double-padding trên mobile 375px - xem comment ở nhánh return cuối file). Ở CourseDashboard.js
// (khối top-level toàn cơ sở, đứng cùng cấp CourseNotificationsPanel ngay phía trên) áp dụng y
// nguyên bộ class đó khiến khối mất hẳn khung/nền ở màn hình rộng - lệch hẳn so với các khối khác
// cùng trang (bạn báo lỗi này, kèm ảnh). standaloneCard=true đổi qua CÙNG bộ class khung ngoài
// CourseNotificationsPanel.js đang dùng (p-4 md:p-6 + border/rounded-3xl/shadow-sm ở MỌI breakpoint)
// - khớp 100% khối liền kề, không tự chế class song song. KHÔNG đổi default false (giữ nguyên hành
// vi CourseClassTimetableTab.js/CourseClassOperations.js).
const CourseSchedulePanel = ({
    owner_id, owner_type, course_id, course_class_id, eyebrow, title,
    hideEyebrow = false, hideActionLinks = false, collapsible = false, compactUpcomingOnly = false,
    standaloneCard = false,
}) => {
    const t = useTranslate()
    const isSingleClass = !!course_class_id
    const viewModes = isSingleClass ? CLASS_VIEW_MODES : VIEW_MODES
    const [viewMode, setViewMode] = useState(isSingleClass ? 'upcoming' : 'today')
    // "Ban đầu để collapse thu gọn" - mặc định true khi collapsible, vô hiệu lực khi !collapsible.
    const [collapsed, setCollapsed] = useState(true)

    const {response, isLoading} = useAsync({
        apiInfo: timetable_getsByClassesOwner_Api,
        query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
        values: {status: 1, course_id, course_class_id},
        deps: [owner_id, owner_type, course_id, course_class_id],
    })
    const sessions = useMemo(() => flattenSessions(getResponseItems(response) ?? []), [response])

    // Buổi sắp tới gần nhất trong TOÀN BỘ sessions (không lọc theo hôm nay như TodayView) - cùng
    // điều kiện "còn hiệu lực" với ListView (chưa is_holiday, end_time chưa qua), chỉ lấy phần tử
    // đầu sau khi sort tăng dần theo start_time. CHỈ dùng làm bản xem trước lúc THU GỌN (xem
    // collapsedPreview bên dưới) - lúc BUNG RA vẫn hiện đầy đủ Hôm nay/List/Calendar như cũ, KHÔNG
    // thay thế hẳn (bạn đã chỉnh lại đúng ý này - trước đó hiểu nhầm thành ẩn luôn cả lúc bung ra).
    const nextSession = useMemo(() => {
        if (!compactUpcomingOnly) return null
        return _.chain(sessions)
            .filter((s) => !s.is_holiday && dayjs(s.end_time).isAfter(dayjs()))
            .sortBy((s) => dayjs(s.start_time).valueOf())
            .first()
            .value()
    }, [sessions, compactUpcomingOnly])

    // Toggle Hôm nay/List/Calendar chỉ có tác dụng lên bodyContent (nội dung ĐẦY ĐỦ, chỉ hiện lúc
    // bung ra) - ẩn khỏi header lúc đang thu gọn (bấm cũng vô nghĩa vì nội dung nó điều khiển đang
    // ẩn), hiện lại ngay khi bung ra.
    const showViewModeToggle = !collapsible || !collapsed

    const headerContent = (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
                {collapsible && (
                    <span
                        className="mt-0.5 flex flex-center flex-shrink-0 rounded-full border border-amber-200 bg-white"
                        style={{width: 28, height: 28}}>
                        <IoChevronDown
                            size={15}
                            style={{color: ORANGE}}
                            className={classNames('transition-transform duration-200', !collapsed && 'rotate-180')}
                        />
                    </span>
                )}
                <div className="flex flex-col gap-0.5 min-w-0">
                    {!hideEyebrow && (
                        <span className="flex items-center gap-1.5 text-xs font-bold text-orange-600 uppercase tracking-wide">
                            <BsDoorOpen size={12}/>
                            {t(eyebrow || 'schedule management for whole center')}
                        </span>
                    )}
                    <h3 className="font-bold text-lg md:text-xl text-slate-800 tracking-wide">
                        {t(title || 'schedule and room statistics')}
                    </h3>
                </div>
            </div>
            {showViewModeToggle && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="p-1 flex items-center gap-1 rounded-xl border border-color-50 shrink-0 max-w-full overflow-x-auto">
                    {viewModes.map((mode) => {
                        const isActive = viewMode === mode.key
                        const Icon = mode.icon
                        return (
                            <button
                                key={mode.key}
                                type="button"
                                onClick={() => setViewMode(mode.key)}
                                style={{background: isActive ? ORANGE : undefined, color: isActive ? '#fff' : undefined}}
                                className={classNames(
                                    'px-3 py-1.5 flex items-center gap-1.5 rounded-lg text-xs font-bold whitespace-nowrap cursor-pointer border-0 flex-shrink-0 transition-colors duration-200',
                                    !isActive && 'text-slate-500 hover:background-100',
                                )}>
                                <Icon size={13}/>
                                {mode.key === 'today' ? `${t(mode.label)} (${dayjs().format('DD/MM')})` : t(mode.label)}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )

    // Nội dung ĐẦY ĐỦ - y hệt hành vi gốc trước khi có collapsible/compactUpcomingOnly, chỉ hiện khi
    // KHÔNG collapsible hoặc đã bung ra.
    const bodyContent = viewMode === 'upcoming' ? (
        <MyClassSessionsPanel/>
    ) : isLoading && !sessions.length ? (
        <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
                <div key={i} style={{height: 60}} className="w-full rounded-xl background-200 animate-pulse"/>
            ))}
        </div>
    ) : viewMode === 'today' ? (
        <TodayView sessions={sessions} t={t} hideActionLinks={hideActionLinks}/>
    ) : viewMode === 'list' ? (
        <ListView sessions={sessions} t={t} hideActionLinks={hideActionLinks}/>
    ) : (
        <CalendarView sessions={sessions} t={t}/>
    )

    // Bản xem trước lúc THU GỌN - đúng 1 buổi sắp tới gần nhất (hoặc skeleton lúc đang tải, hoặc
    // thông báo rỗng) - hiện NGAY trong header, không cần bấm mở mới thấy gì.
    const collapsedPreview = isLoading ? (
        <div style={{height: 68}} className="w-full rounded-xl background-200 animate-pulse"/>
    ) : nextSession ? (
        <TodaySessionCard session={nextSession} t={t} hideActionLinks={hideActionLinks} showDate/>
    ) : (
        <div className="py-2 text-sm text-slate-300 italic">{t('no upcoming schedule')}</div>
    )

    if (collapsible) {
        // Viền hổ phách - khớp tông khối InfoBlock amber ngay phía trên (CourseClassOperations.js) để
        // 2 khối đọc như 1 nhóm "thông tin cần xem thêm", tách bạch với khối navy "Quản trị Khóa học"
        // chính phía trên cùng.
        return (
            <div className="border border-amber-200 bg-amber-50 rounded-3xl shadow-sm flex flex-col">
                <div
                    onClick={() => setCollapsed((v) => !v)}
                    className="p-4 flex flex-col gap-3 cursor-pointer">
                    {headerContent}
                    {collapsed && compactUpcomingOnly && (
                        <div onClick={(e) => e.stopPropagation()} className="cursor-default">
                            {collapsedPreview}
                        </div>
                    )}
                </div>
                {!collapsed && (
                    <div className="px-4 pb-4">
                        {bodyContent}
                    </div>
                )}
            </div>
        )
    }

    return (
        // Mobile: bỏ hẳn khung/nền/viền riêng của card NGOÀI CÙNG này (chỉ px-0, giữ nguyên padding
        // dọc) - trước đây p-4 (16px) của card này CỘNG DỒN với padding trang (CourseClassPage.js,
        // px-2) VÀ viền/padding riêng của từng SessionCard bên trong -> 3 lớp khung lồng nhau, nội
        // dung bị bóp hẹp hẳn vào giữa trên 375px (bạn báo lỗi này). Từ sm+ (tablet/desktop, đủ chỗ)
        // mới hiện lại card bo góc/viền/shadow như cũ. standaloneCard: bỏ qua toàn bộ tuning riêng
        // cho ngữ cảnh lồng đó, dùng thẳng khung card chuẩn (khớp CourseNotificationsPanel.js) - xem
        // comment standaloneCard ở khai báo prop phía trên.
        <div
            className={
                standaloneCard
                    ? 'p-4 md:p-6 background border border-color-50 rounded-3xl shadow-sm flex flex-col gap-4'
                    : 'px-0 py-4 sm:p-3 lg:p-0 sm:background sm:border sm:border-color-50 sm:rounded-3xl sm:shadow-sm flex flex-col gap-4'
            }>
            {headerContent}
            {bodyContent}
        </div>
    )
}

export default CourseSchedulePanel
